import { Button, Icon, Input, Modal, Textarea, useToast } from '@elva/ui';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { keys } from '@/api/queryKeys';
import { extractApiErrorMessage } from '@/api/errors';
import { formsApi } from '../api/formsApi';
import type { FormTriggerParseResult } from '../api/types';
import {
  useConfirmTriggerParserMutation,
  useFormTriggerParser,
  useParseTriggerParserMutation,
} from '../hooks/useFormTriggerRules';

export interface FormSettingsDialogProps {
  open: boolean;
  formId: string | null;
  initialName: string;
  initialDescription?: string;
  /** First-time create flow — save label and success copy differ. */
  variant?: 'settings' | 'create';
  onClose: () => void;
  onSaved: (data: { formId: string; name: string; description: string }) => void;
}

function isStalePreviewError(error: unknown): boolean {
  if (!isAxiosError(error) || error.response?.status !== 409) return false;
  const data = error.response.data as { error?: string } | undefined;
  return data?.error === 'stale_preview';
}

export function FormSettingsDialog({
  open,
  formId,
  initialName,
  initialDescription = '',
  variant = 'settings',
  onClose,
  onSaved,
}: FormSettingsDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: parserData, isLoading: parserLoading } = useFormTriggerParser(open ? formId : null);
  const parseTrigger = useParseTriggerParserMutation(formId ?? '');
  const confirmTrigger = useConfirmTriggerParserMutation(formId ?? '');

  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [triggerRules, setTriggerRules] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // W22 — parse/confirm handshake state.
  const [preview, setPreview] = useState<FormTriggerParseResult | null>(null);
  const [previewText, setPreviewText] = useState('');
  const [triggerError, setTriggerError] = useState<string | null>(null);
  const [confirmedNow, setConfirmedNow] = useState(false);
  const [committedText, setCommittedText] = useState('');
  const [loadedConfirmedAt, setLoadedConfirmedAt] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(initialName);
    setDescription(initialDescription);
  }, [open, initialName, initialDescription]);

  useEffect(() => {
    if (!open || parserLoading) return;
    const raw = parserData?.raw_text ?? '';
    setTriggerRules(raw);
    setCommittedText(raw);
    setLoadedConfirmedAt(parserData?.confirmed_at ?? null);
    setPreview(null);
    setTriggerError(null);
    setConfirmedNow(false);
  }, [open, parserData, parserLoading]);

  if (!open) return null;

  const trimmedTrigger = triggerRules.trim();
  const previewStale = preview !== null && previewText !== trimmedTrigger;
  const interpretation =
    preview !== null && !previewStale && !preview.unsupported && triggerError === null ? preview : null;
  const showPreviewButton =
    formId !== null && trimmedTrigger.length > 0 && interpretation === null && !confirmedNow;
  const showUnconfirmedHint =
    !confirmedNow && loadedConfirmedAt === null && committedText.trim().length > 0 && interpretation === null;

  const onTriggerTextChange = (value: string) => {
    setTriggerRules(value);
    setTriggerError(null);
    setConfirmedNow(false);
  };

  const handlePreview = async () => {
    if (!formId || !trimmedTrigger) return;
    setTriggerError(null);
    setConfirmedNow(false);
    try {
      const result = await parseTrigger.mutateAsync(trimmedTrigger);
      setPreview(result);
      setPreviewText(trimmedTrigger);
      setCommittedText(trimmedTrigger); // parse stores raw_prompt server-side
      if (result.unsupported) setTriggerError(result.description_text);
    } catch (error) {
      toast({ title: 'Couldn’t read the trigger', description: extractApiErrorMessage(error), variant: 'error' });
    }
  };

  const handleAgree = async () => {
    if (!formId || !preview) return;
    try {
      await confirmTrigger.mutateAsync({ raw_prompt: previewText, preview_hash: preview.preview_hash });
      setCommittedText(previewText);
      setLoadedConfirmedAt(new Date().toISOString());
      setConfirmedNow(true);
      setPreview(null);
      toast({ title: 'Trigger confirmed', variant: 'success' });
    } catch (error) {
      if (isStalePreviewError(error)) {
        // 409: the interpretation changed since preview — re-parse and re-show. Never retry commit.
        try {
          const fresh = await parseTrigger.mutateAsync(previewText);
          setPreview(fresh);
          setPreviewText(previewText);
          setCommittedText(previewText);
          setTriggerError(fresh.unsupported ? fresh.description_text : null);
          toast({ title: 'Interpretation changed — review it again', variant: 'warning' });
        } catch (reparseError) {
          toast({ title: 'Parse failed', description: extractApiErrorMessage(reparseError), variant: 'error' });
        }
        return;
      }
      // e.g. the parser declined on re-check — surface its explanation verbatim.
      setTriggerError(extractApiErrorMessage(error));
    }
  };

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast({ title: 'Name is required', variant: 'error' });
      return;
    }

    setIsSaving(true);
    try {
      let id = formId;
      const trimmedDescription = description.trim();

      if (!id) {
        const created = await formsApi.createTemplate({
          name: trimmedName,
          ...(trimmedDescription ? { description: trimmedDescription } : {}),
        });
        id = created.id;
      } else {
        await formsApi.updateTemplate(id, {
          name: trimmedName,
          ...(trimmedDescription ? { description: trimmedDescription } : { description: '' }),
        });
      }

      // Store the trigger text only when it changed — re-storing confirmed text
      // would clear its confirmation (backend `update_raw_prompt`). Rules are
      // written exclusively by Agree (confirm), never by Save.
      if (trimmedTrigger !== committedText) {
        await formsApi.putTriggerParser(id, { raw_text: trimmedTrigger });
        setCommittedText(trimmedTrigger);
      }

      void queryClient.invalidateQueries({ queryKey: keys.forms.template(id) });
      void queryClient.invalidateQueries({ queryKey: keys.forms.triggerParser(id) });
      void queryClient.invalidateQueries({ queryKey: keys.forms.templates });
      void queryClient.invalidateQueries({ queryKey: keys.formsStats });

      onSaved({ formId: id, name: trimmedName, description: trimmedDescription });
      toast({
        title: variant === 'create' ? 'Draft saved' : 'Form settings saved',
        variant: 'success',
      });
    } catch (error) {
      toast({ title: 'Save failed', description: extractApiErrorMessage(error), variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const triggerBusy = parseTrigger.isPending || confirmTrigger.isPending;

  return (
    <Modal
      title={variant === 'create' ? 'New form' : 'Form settings'}
      onClose={onClose}
      className="w-[min(520px,calc(100vw-32px))]"
      footer={
        <>
          <Button variant="secondary" size="sm" disabled={isSaving} onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            iconLeft="check"
            disabled={isSaving || (formId != null && parserLoading)}
            onClick={() => {
              void handleSave();
            }}
          >
            {isSaving ? 'Saving…' : variant === 'create' ? 'Save draft' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Input
          label="Name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
          }}
          placeholder="Form name"
          autoFocus
        />
        <Textarea
          label="Description"
          value={description}
          onChange={(e) => {
            setDescription(e.target.value);
          }}
          placeholder="What is this form for?"
          rows={3}
        />

        <div className="flex flex-col gap-2">
          <Textarea
            label="Trigger rules"
            value={triggerRules}
            onChange={(e) => {
              onTriggerTextChange(e.target.value);
            }}
            placeholder="e.g. Send to all new patients before their first appointment"
            hint="Describe when ELVA should send this form automatically."
            rows={4}
            disabled={formId != null && parserLoading}
          />

          {formId === null ? (
            <p className="text-[11.5px] text-[var(--ink-4)]">
              Save the form first to preview and confirm automatic triggers.
            </p>
          ) : null}

          {triggerError !== null ? (
            <div className="flex gap-2 rounded-[var(--r-md)] border border-[color-mix(in_srgb,var(--terracotta)_28%,var(--cream-2))] bg-[var(--terracotta-tint)] px-3 py-2.5">
              <Icon kind="alert" size={14} className="mt-0.5 shrink-0 text-[var(--terracotta)]" />
              <p className="text-[12px] leading-relaxed text-[var(--ink-1)]">{triggerError}</p>
            </div>
          ) : interpretation !== null ? (
            <div className="rounded-[var(--r-md)] border border-[color-mix(in_srgb,var(--ai-3)_25%,var(--cream-2))] bg-[var(--ai-tint)] px-3 py-2.5">
              <p className="mb-1 text-[10.5px] font-semibold uppercase tracking-wide text-[var(--ai-3)]">
                ELVA read this as
              </p>
              <p className="whitespace-pre-wrap text-[12.5px] leading-relaxed text-[var(--ink-1)]">
                {interpretation.description_text}
              </p>
              <div className="mt-2.5 flex items-center justify-end gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={triggerBusy}
                  onClick={() => {
                    setPreview(null);
                  }}
                >
                  Edit
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  iconLeft="check"
                  disabled={triggerBusy}
                  onClick={() => {
                    void handleAgree();
                  }}
                >
                  {confirmTrigger.isPending ? 'Confirming…' : 'Agree'}
                </Button>
              </div>
            </div>
          ) : confirmedNow ? (
            <div className="flex items-center gap-2 rounded-[var(--r-md)] border border-[color-mix(in_srgb,var(--success)_28%,var(--cream-2))] bg-[color-mix(in_srgb,var(--success)_8%,var(--cream-1))] px-3 py-2">
              <Icon kind="check" size={13} className="shrink-0 text-[var(--success)]" />
              <p className="text-[12px] text-[var(--ink-1)]">Trigger confirmed — ELVA will send this form automatically.</p>
            </div>
          ) : showPreviewButton ? (
            <div className="flex items-center justify-between gap-2">
              {showUnconfirmedHint ? (
                <span className="text-[11.5px] text-[var(--ink-4)]">Not confirmed yet — preview to review.</span>
              ) : (
                <span />
              )}
              <Button
                variant="secondary"
                size="sm"
                disabled={triggerBusy}
                onClick={() => {
                  void handlePreview();
                }}
              >
                {parseTrigger.isPending ? 'Reading…' : 'Preview interpretation'}
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
